import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, renderList as _renderList, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "d-flex flex-column" }
const _hoisted_2 = { class: "d-flex align-items-center" }
const _hoisted_3 = {
  key: 0,
  class: "caption text-grey-700 text-nowrap ms-2 d-flex align-items-center"
}
const _hoisted_4 = {
  key: 0,
  class: "caption-strong mt-2 d-flex align-items-center"
}
const _hoisted_5 = {
  key: 0,
  class: "mt-2 subcaption d-flex align-items-center"
}
const _hoisted_6 = { class: "d-flex align-items-center gap-2" }
const _hoisted_7 = { class: "title-3" }

import { computed, ComputedRef, ref, Ref, onMounted } from "vue"
import type { ComponentPublicInstance } from "vue"
import moment from "moment"

// Utils
import { isScreenMD } from "@/vue/planning/utils/breakpoints"
import formatDateString from "@/vue/planning/utils/formatDate"

// Components
import FlexiblePaymentConditionPill from "./FlexiblePaymentConditionPill.vue"
import PlaceholderBox from "@/vue/planning/components/common/placeholderBox.vue"
import PackageDetailsTooltip from "./PackageDetailsTooltip.vue"
import DynamicPopup from "@/vue/common/DynamicPopup.vue"
import PaymentConditionPopup from "./PaymentConditionPopup.vue"
import TermsAndConditions from "./TermsAndConditions.vue"

// Composables
import useLocale from "@/vue/composables/useLocale"

interface Props {
	displayTitle: boolean
	content: { [key: string]: string }
	isPVFlex?: boolean
	prestId?: string
	isDisplayPlaceholders?: boolean
	cancellationDate?: string
	startDate?: string
	isCompactDisplay?: boolean
}

type DynamicPopupInstance = ComponentPublicInstance<typeof DynamicPopup>


export default /*@__PURE__*/_defineComponent({
  __name: 'FlexiblePaymentCondition',
  props: {
    displayTitle: { type: Boolean },
    content: {},
    isPVFlex: { type: Boolean },
    prestId: {},
    isDisplayPlaceholders: { type: Boolean },
    cancellationDate: {},
    startDate: {},
    isCompactDisplay: { type: Boolean }
  },
  setup(__props: any) {

const locale = useLocale()

// Props
const props = __props

// Refs
const popupRef: Ref<DynamicPopupInstance | null> = ref(null)

// Computeds
const isStartDateMoreThen40Days: ComputedRef<boolean> = computed(() => {
	if (!props.startDate) {
		return false // Handle cases where startDate might not be provided
	}


	const startDate = moment(props.startDate, 'DD/MM/YYYY', true)
	const today = moment()

	if (!startDate.isValid()) {
		console.error("Invalid date format for startDate: ", props.startDate);
		return false;
	}

	// Calculate the difference in days
	const diffDays = startDate.diff(today, 'days')
	return diffDays > 40
})

const content: ComputedRef<{ [key: string]: string }> = computed(() => {
	return props.content
})

const formattedCancellationDate: ComputedRef<string> = computed(() => {
	return props.cancellationDate ? formatDateString(props.cancellationDate) : ""
})

const baseTitle: ComputedRef<string> = computed(() => {
	const base = props.isPVFlex
		? content.value.flex_line_1_PV
		: content.value.flex_line_1
		
	return `${base} ${formattedCancellationDate.value}${content.value.flex_line_2 || ''}`
})

const title: ComputedRef<string> = computed(() => {
	return baseTitle.value
})

// Methods
const getPVCancellationPolicy = (): string => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return `${
		content.value.flex_picto_PV_cancelation_until ?? ""
	} 
	${cancellationDateFormatted}
		
	${
		content.value.flex_picto_PV_i_terms_conditions_1 ?? ""
	} 
	`
}

const getPVFirstSentence = (): string => {
	return isStartDateMoreThen40Days.value
		? content.value.flex_picto_PV_i_payment_condition_before_deposit
		: content.value.flex_picto_PV_i_payment_condition_after_deposit
}

const getDefaultSentences = (): string[] => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return [
		`${content.value.flex_picto_i_1 ?? ""} ${locale !== "nl_NL" ? cancellationDateFormatted: ''} ${
			content.value.flex_picto_i_1_2 ?? ""
		}`,
		`${
			content.value.flex_picto_i_terms_conditions_1 ?? ""
		}`,
	]
}

// Computed property using helper methods with TypeScript type
const sentences: ComputedRef<string[]> = computed(() => {
	if (props.isPVFlex) {
		return [getPVFirstSentence(), getPVCancellationPolicy()]
	} else {
		return getDefaultSentences()
	}
})

// Methods
const handleOpenPopup = (): void => {
	// Only open the popup if the device is mobile
	if (isScreenMD.value) return

	if (popupRef.value) {
		popupRef.value.openPopup()
	}
}

const handleClosePopUp = () => {
	if (popupRef.value) {
		popupRef.value.closePopup()
	}
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(FlexiblePaymentConditionPill, {
          content: props.content
        }, null, 8, ["content"]),
        (props.prestId)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
              (_ctx.isDisplayPlaceholders)
                ? (_openBlock(), _createBlock(PlaceholderBox, {
                    key: 0,
                    height: '25px',
                    width: '100px'
                  }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode(" Ref: " + _toDisplayString(props.prestId), 1)
                  ], 64))
            ]))
          : _createCommentVNode("", true)
      ]),
      (props.displayTitle && !_ctx.isCompactDisplay)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(props.content.flex_title), 1))
        : _createCommentVNode("", true)
    ]),
    (!_ctx.isCompactDisplay)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(PackageDetailsTooltip, { onClick: handleOpenPopup }, {
            default: _withCtx(() => [
              _createElementVNode("div", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sentences.value, (sentence, sentence_i) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: `${sentence[0]}-${sentence_i}`
                  }, _toDisplayString(sentence), 1))
                }), 128)),
                _createVNode(TermsAndConditions)
              ])
            ]),
            _: 1
          }),
          _createElementVNode("div", null, [
            _createTextVNode(_toDisplayString(title.value) + " ", 1),
            _createVNode(TermsAndConditions)
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(DynamicPopup, {
      ref_key: "popupRef",
      ref: popupRef,
      "overlay-color": 'rgba(13, 20, 27, 0.95)',
      "overlay-max-opacity": 1
    }, {
      default: _withCtx(() => [
        _createVNode(PaymentConditionPopup, { onClose: handleClosePopUp }, {
          title: _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(FlexiblePaymentConditionPill, {
                content: props.content,
                height: 30
              }, null, 8, ["content"]),
              _createElementVNode("div", _hoisted_7, _toDisplayString(props.content.flex_title), 1)
            ])
          ]),
          content: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sentences.value, (sentence, sentence_i) => {
              return (_openBlock(), _createElementBlock("span", {
                key: `${sentence[0]}-${sentence_i}`
              }, _toDisplayString(sentence), 1))
            }), 128)),
            _createVNode(TermsAndConditions)
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 512)
  ]))
}
}

})